// src/pages/AdminBenefits.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "../services/api";
import { useNavigate } from "react-router-dom";
import PageShell from "../components/PageShell";

/* ---------------- Config ---------------- */

const BENEFIT_GROUPS = {
  ENTRY: [
    "EVENT_ACCESS",
    "PLAYLIST_FEATURE",
    "SOCIAL_REEL",
    "TOP_5_FEATURE",
    "TALENT_ROSTER",
  ],
  MIDDLE: [
    "PLAYLIST_FEATURE_SECOND",
    "SOCIAL_POST",
    "LARGER_EVENT_INVITE",
    "NETWORK_EVENT_ACCESS",
  ],
  HEADLINER: [
    "HEADLINE_OPPORTUNITIES",
    "CUSTOM_PROMOTION",
    "CAREER_GUIDANCE",
    "STUDIO_SESSION_MONTHLY",
    "DJ_LIVESTREAM",
  ],
};

const BENEFIT_LABEL = {
  EVENT_ACCESS: "Event Access",
  PLAYLIST_FEATURE: "Permanent feature on 1 curated playlist",
  PLAYLIST_FEATURE_SECOND: "Permanent feature on a 2nd curated playlist",
  SOCIAL_REEL: "Dedicated reel (IG, YouTube, TikTok, X)",
  TOP_5_FEATURE: "Eligibility: Top 5 Songs of the Week",
  TALENT_ROSTER: "Consideration for future events (on our books)",
  SOCIAL_POST: "Additional social post(s)",
  LARGER_EVENT_INVITE: "Invitations to perform at larger events",
  NETWORK_EVENT_ACCESS: "Industry networking event access",
  HEADLINE_OPPORTUNITIES: "Opportunities to headline major events",
  CUSTOM_PROMOTION: "Advanced, personalised promotional campaign",
  CAREER_GUIDANCE: "Comprehensive career guidance",
  STUDIO_SESSION_MONTHLY: "Monthly access to a state-of-the-art studio",
  DJ_LIVESTREAM: "1-hour DJ set, broadcast live",
  STUDIO_SESSION: "Studio Session (legacy)",
};

const TIER_BADGE = {
  ENTRY: "badge bg-white/10 text-slate-200",
  MIDDLE: "badge bg-brand-600/20 text-slate-100",
  HEADLINER: "badge bg-emerald-500/20 text-emerald-200",
};

/* ---------------- Utils ---------------- */

function useDebounced(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

/* ---------------- Page ---------------- */

export default function AdminBenefits() {
  const navigate = useNavigate();

  // list state
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // search + sort
  const [emailQuery, setEmailQuery] = useState("");
  const debouncedEmail = useDebounced(emailQuery, 500);
  const [sortField, setSortField] = useState("id");
  const [sortDir, setSortDir] = useState("DESC");
  const sortParam = useMemo(() => `${sortField},${sortDir}`, [sortField, sortDir]);

  const [error, setError] = useState("");

  // selected user + benefits
  const [selected, setSelected] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [loadingBenefits, setLoadingBenefits] = useState(false);

  useEffect(() => {
    loadSubscribers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, sortParam, debouncedEmail]);

  async function loadSubscribers() {
    setError("");
    try {
      const { data } = await axios.get("/api/admin/benefits/subscribers", {
        params: { page, size, sort: sortParam, email: debouncedEmail || undefined },
      });
      setItems(data.content || []);
      setTotalPages(data.totalPages ?? 0);
      setTotalElements(data.totalElements ?? 0);
    } catch (e) {
      const msg =
        e?.response?.status === 403
          ? "You must be an ADMIN or MASTER to view this page."
          : e?.response?.data?.message || "Failed to load subscribers.";
      setError(msg);
    }
  }

  async function selectUser(u) {
    setSelected(u);
    setBenefits([]);
    setLoadingBenefits(true);
    try {
      const { data } = await axios.get(`/api/admin/benefits/user/${u.id}`);
      setBenefits(data || []);
    } catch (e) {
      alert(e?.response?.data?.message || "Failed to load benefits for user.");
    } finally {
      setLoadingBenefits(false);
    }
  }

  async function toggleBenefit(b) {
    const isDelivered = !!b.delivered;
    try {
      if (isDelivered) {
        await axios.post(`/api/admin/benefits/undeliver/${b.id}`);
      } else {
        await axios.post(`/api/admin/benefits/deliver/${b.id}`);
      }
      setBenefits((prev) =>
        prev.map((x) =>
          x.id === b.id
            ? {
                ...x,
                delivered: !isDelivered,
                deliveredAt: !isDelivered ? new Date().toISOString().slice(0, 10) : null,
              }
            : x
        )
      );
    } catch (e) {
      alert(e?.response?.data?.message || "Failed to update benefit.");
    }
  }

  function toggleSort(field) {
    if (sortField === field) setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"));
    else {
      setSortField(field);
      setSortDir("ASC");
    }
    setPage(0);
  }

  // Group benefits by tiers for the selected user, respecting business order
  const grouped = useMemo(() => {
    const map = { ENTRY: [], MIDDLE: [], HEADLINER: [] };
    (Object.keys(BENEFIT_GROUPS)).forEach((tier) => {
      const order = BENEFIT_GROUPS[tier];
      map[tier] = order
        .map((name) => benefits.find((b) => b.benefitType === name))
        .filter(Boolean);
    });
    return map;
  }, [benefits]);

  return (
    <PageShell>
    <div className="page space-y-6">
      {/* Header */}
      <section className="card">
        <div className="md:flex md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Benefits Manager</h1>
            <p className="mt-1 text-slate-300">
              Search subscribers, then deliver or revoke benefits per tier.
            </p>
          </div>
          <button onClick={() => navigate(-1)} className="btn-secondary">
            ← Back
          </button>
        </div>

        {error && (
          <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-200">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto]">
          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-slate-400">
              Search subscribers by email
            </span>
            <input
              type="text"
              value={emailQuery}
              onChange={(e) => {
                setEmailQuery(e.target.value);
                setPage(0);
              }}
              placeholder="e.g. user@example.com"
              className="input w-full"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-slate-400">
              Page size
            </span>
            <select
              value={size}
              onChange={(e) => {
                setSize(Number(e.target.value));
                setPage(0);
              }}
              className="input w-full"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {/* Main content: table + details */}
      <section className="grid gap-6 md:grid-cols-2">
        {/* Subscribers table */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-white/[0.03]">
                <tr>
                  <Th label="ID" field="id" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Email" field="email" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Username" field="username" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Tier" field="subscriptionTier" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Active" field="subscriptionActive" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr
                    key={u.id}
                    className={`border-t border-white/10 hover:bg-white/[0.03] cursor-pointer ${
                      selected?.id === u.id ? "bg-brand-600/10" : ""
                    }`}
                    onClick={() => selectUser(u)}
                  >
                    <td className="px-3 py-2">{u.id}</td>
                    <td className="px-3 py-2">{u.email}</td>
                    <td className="px-3 py-2">{u.username}</td>
                    <td className="px-3 py-2">
                      <span className={TIER_BADGE[u.subscriptionTier] || "badge bg-white/10 text-slate-200"}>
                        {u.subscriptionTier || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {u.subscriptionActive ? (
                        <span className="badge bg-emerald-500/20 text-emerald-200">Active</span>
                      ) : (
                        <span className="badge bg-white/10 text-slate-300">Inactive</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between gap-2 border-t border-white/10 px-3 py-2 text-sm">
            <div>
              Page <span className="font-medium text-white">{page + 1}</span> of{" "}
              <span className="font-medium text-white">{Math.max(totalPages, 1)}</span> • Total{" "}
              <span className="font-medium text-white">{totalElements}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page <= 0}
                className="btn-secondary disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
                disabled={page + 1 >= totalPages}
                className="btn-secondary disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Selected user's benefits */}
        <div className="card">
          {!selected ? (
            <div className="text-slate-300">Select a subscriber to manage benefits.</div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-white">{selected.email}</h2>
                  <div className="mt-0.5 text-slate-400 text-sm">
                    Username: <span className="text-slate-200">{selected.username || "—"}</span>
                  </div>
                </div>
                <span className={TIER_BADGE[selected.subscriptionTier] || "badge bg-white/10 text-slate-200"}>
                  {selected.subscriptionTier || "—"}
                </span>
              </div>

              {loadingBenefits ? (
                <div className="mt-4 grid gap-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 animate-pulse">
                      <div className="h-4 w-1/3 bg-white/5 rounded" />
                      <div className="mt-2 h-3 w-full bg-white/5 rounded" />
                      <div className="mt-2 h-3 w-2/3 bg-white/5 rounded" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 space-y-6">
                  {["ENTRY", "MIDDLE", "HEADLINER"].map((tier) => (
                    <div key={tier} className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <h3 className="font-medium text-white">{tier} Benefits</h3>
                        <span className="text-xs text-slate-400">
                          {grouped[tier].length} item{grouped[tier].length === 1 ? "" : "s"}
                        </span>
                      </div>

                      {grouped[tier].length === 0 ? (
                        <p className="text-slate-400 text-sm">No benefits in this group.</p>
                      ) : (
                        <ul className="space-y-2">
                          {grouped[tier].map((b) => {
                            const on = !!b.delivered;
                            return (
                              <li key={b.id} className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="text-white truncate">
                                    {BENEFIT_LABEL[b.benefitType] || b.benefitType}
                                  </div>
                                  <div className="text-xs text-slate-400">
                                    {on ? `Delivered ${fmtDate(b.deliveredAt)}` : "Undelivered"}
                                  </div>
                                </div>

                                {/* Toggle pill */}
                                <button
                                  type="button"
                                  onClick={() => toggleBenefit(b)}
                                  className={`relative inline-flex h-7 w-14 items-center rounded-full border transition
                                    ${on ? "bg-emerald-600/80 border-emerald-500/40" : "bg-white/10 border-white/15"}`}
                                  aria-pressed={on}
                                >
                                  <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition
                                      ${on ? "translate-x-7" : "translate-x-1"}`}
                                  />
                                  <span className="sr-only">Toggle delivered</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
    </PageShell>
  );
}

/* ---------------- Bits ---------------- */

function Th({ label, field, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <th
      className="px-3 py-2 text-left cursor-pointer select-none text-slate-300"
      onClick={() => onSort(field)}
      title="Click to sort"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && <span className="text-xs">{sortDir === "ASC" ? "▲" : "▼"}</span>}
      </span>
    </th>
  );
}
